import { inject, Injectable, signal } from '@angular/core';
import { ToastType } from '@app/enums/toast-type.enum';
import { AppStore } from '@app/stores/app/app.store';

interface Toast {
    id: number;
    title: string;
    type: ToastType;
    message: string;
    hasSingleButton?: boolean;
    hasDoubleButton?: boolean;
    duration?: number;
}

@Injectable({
    providedIn: 'root',
})
export class ToastService {
    private toastCounter = signal(0);
    private maxVisibleToasts = signal(3);
    private appStore = inject(AppStore);
    translate = this.appStore.i18n;
    toasts = signal<Toast[]>([]);

    addToast(
        type: ToastType,
        message: string,
        title = '',
        duration = 6000,
        hasSingleButton = false,
        hasDoubleButton = false,
    ): void {
        const newId = this.toastCounter() + 1;

        const toast: Toast = {
            id: newId,
            title: title ? title : this.getToastDefaultTitle(type),
            message,
            type,
            duration,
            hasSingleButton,
            hasDoubleButton,
        };

        this.toastCounter.set(newId); // Update the toast counter
        this.toasts.update((currentToasts) => {
            if (currentToasts.length >= this.maxVisibleToasts()) {
                currentToasts.shift(); // Remove the oldest toast
            }
            return [...currentToasts, toast];
        });

        // Automatically remove the toast after its duration
        setTimeout(() => {
            this.removeToast(toast.id);
        }, duration);
    }

    getToastDefaultTitle(type: ToastType): string {
        const { Success, Warning, Information, Error } = this.translate();
        switch (type) {
            case ToastType.Success:
                return Success;
            case ToastType.Warning:
                return Warning;
            case ToastType.Info:
                return Information;
            case ToastType.Error:
                return Error;
            default:
                return '';
        }
    }

    removeToast(id: number): void {
        // Update the signal by filtering out the removed toast
        this.toasts.update((currentToasts) =>
            currentToasts.filter((toast) => toast.id !== id),
        );
    }
}
